/**
 * HTML builder for weekly calendar layout
 * Handles all HTML generation for the weekly calendar widget
 */

import type { WidgetConfig, ProfessionalSummary } from '../../types';
import {
  toYMD,
  fromYMD,
  formatWeekday,
  formatShortMDY,
  formatWeekRange,
  getPositionAcronym,
  buildProfileUrl,
  buildShowMoreUrl,
  formatProfessionalName,
  escapeHtml,
  isOnDiemDomain,
  shouldShowPartnerBranding,
} from '../../utils';
import { CDN_ASSETS, POSITION_TYPES, ONDIEM_LOGO_SVG } from '../../config';

export class WeeklyCalendarHTMLBuilder {
  private config: WidgetConfig;
  private prosByDate: Record<string, ProfessionalSummary[]>;
  private todayYMD: string;
  private weekOffset: number;
  private isPartner: boolean;

  constructor(
    config: WidgetConfig,
    prosByDate: Record<string, ProfessionalSummary[]>,
    todayYMD: string,
    weekOffset: number = 0
  ) {
    this.config = config;
    this.prosByDate = prosByDate;
    this.todayYMD = todayYMD;
    this.weekOffset = weekOffset;
    this.isPartner = shouldShowPartnerBranding(config.branding);
  }

  /**
   * Builds the complete HTML structure
   */
  buildHTML(start: string, end: string): string {
    const utmContent = this.config.utmContent || '';
    return `
      <div class="ondiem-pro-availability-widget${this.isPartner ? ' is-partner' : ''}">
        <div data-utm-content="${escapeHtml(utmContent)}" class="pro-availability">
          ${this.buildFilterForm(start, end)}
          <div class="w-embed">
            <style>
              select { 
                appearance: none !important; 
                -webkit-appearance: none !important; 
                -moz-appearance: none !important; 
              } 
            </style>
          </div>
          <div class="padding-bottom padding-xmedium"></div>
            ${this.buildLoader()}
            <div id="weekly-slider" class="pro-availability_weekly-calendar-wrapper pro-availability_host padding-global">
              <div class="pro-availability_tabs" id="days-tabs" role="tablist"></div>
              ${this.buildCalendar(start)}
            </div>
          <div class="padding-bottom padding-xmedium"></div>
        </div>
      </div>
    `;
  }

  /**
   * Builds filter form HTML
   */
  private buildFilterForm(start: string, end: string): string {
    const zipValue = this.config.zipCode || '';
    const positionValue = this.config.position || '';

    return `
      <div class="pro-availability_filter-wrapper padding-global">
        <div class="pro-availability_form-container container-large">
          <div class="pro-availability_filter-form w-form">
            <form id="ondiem-filter-form" name="wf-form-Position-Type" data-name="Position Type" method="get" class="pro-availability_filter-form-wrapper" aria-label="Position Type">
              <div class="heading-style-h6">${this.buildHeading()}</div>
              <div class="form_field-group">
                <div class="zip-field-wrapper">
                  <input
                    type="text"
                    id="ondiem-zip-input"
                    class="form_input with-icon is-square w-input"
                    maxlength="5"
                    inputmode="numeric"
                    name="Zip-Code"
                    data-name="Zip Code"
                    placeholder="Zip Code"
                    value="${escapeHtml(zipValue)}"
                    autocomplete="postal-code"
                  />
                  <p id="ondiem-zip-error" class="zip-error-msg" role="alert" aria-live="polite">Enter a valid zip code</p>
                </div>
                <select
                  id="ondiem-position-select"
                  name="Position-Type"
                  data-name="Position Type"
                  class="form_input is-select-input is-square w-select"
                >
                  <option value="">Position Type</option>
                  ${POSITION_TYPES.map(
                    (pos: { value: string; label: string }) =>
                      `<option value="${escapeHtml(pos.value)}" ${positionValue === pos.value ? 'selected' : ''}>${escapeHtml(pos.label)}</option>`
                  ).join('')}
                </select>
              </div>
            </form>
            <div class="w-form-done" tabindex="-1" role="region" aria-label="Position Type success">
              <div>Thank you! Your submission has been received!</div>
            </div>
            <div class="w-form-fail" tabindex="-1" role="region" aria-label="Position Type failure">
              <div>Oops! Something went wrong while submitting the form.</div>
            </div>
          </div>
          ${this.buildWeekNavigation(start, end)}
        </div>
      </div>
    `;
  }

  /**
   * Builds the filter bar heading. On partner sites it carries the
   * "powered by [onDiem]" lockup; on ondiem.com it's the bare heading.
   */
  private buildHeading(): string {
    if (!this.isPartner) {
      return 'Find staff ready to work';
    }

    return `
      <span>Find staff ready to work</span>
      <span class="pro-availability_powered-by">
        powered by
        <span class="pro-availability_powered-by-logo" role="img" aria-label="onDiem">${ONDIEM_LOGO_SVG}</span>
      </span>
    `;
  }

  /**
   * Builds week navigation HTML
   */
  private buildWeekNavigation(start: string, end: string): string {
    // Show prev button only if weekOffset > 0 (not on current week)
    const canGoPrev = this.weekOffset > 0;
    // The partner design keeps the left chevron in place on the current week rather
    // than hiding it, so the date reads as "‹ range ›". It's muted and not clickable
    // there — onPrevWeek() already refuses to go below offset 0.
    const prevClass = `pro-availability_slider-left-arrow${canGoPrev ? '' : ' is-disabled'}`;
    const prevStyle = !canGoPrev && !this.isPartner ? ' style="visibility: hidden;"' : '';
    return `
      <div class="pro-availability_weekly-nav-wrapper">
        <div id="prev-week-btn" class="${prevClass}"${prevStyle}>
          <img src="${CDN_ASSETS.ARROW_LEFT}" loading="lazy" alt="">
        </div>
        <div id="week-range-label" class="heading-style-h6">${escapeHtml(formatWeekRange(start, end))}</div>
        <div id="next-week-btn" class="pro-availability_slider-right-arrow">
          <img src="${CDN_ASSETS.ARROW_RIGHT}" loading="lazy" width="Auto" height="Auto" alt="">
        </div>
      </div>
    `;
  }

  /**
   * Builds loader HTML
   */
  private buildLoader(): string {
    return `
      <div id="calendar-loader" class="calendar-loader hidden">
        <div class="spinner"></div>
      </div>
    `;
  }

  /**
   * Builds calendar HTML
   */
  private buildCalendar(start: string): string {
    const dates: string[] = [];
    let current = fromYMD(start);
    for (let i = 0; i < 7; i++) {
      dates.push(toYMD(current));
      current.setDate(current.getDate() + 1);
    }

    const cards = dates.map((date, index) => this.buildDayCard(date, index * 100));

    return `
      <div id="days-container" data-instance="${escapeHtml(this.config.instance || '')}" class="pro-availability_weekly-cards-wrapper container-large">
        ${cards.join('')}
      </div>
    `;
  }

  /**
   * Builds a single day card HTML
   */
  private buildDayCard(date: string, animationDelay: number): string {
    const isPast = date < this.todayYMD;
    const pros = this.prosByDate[date] || [];
    const hasMore = pros.length > 5;
    const displayPros = pros.slice(0, 5);

    const weekday = formatWeekday(date, 'long');
    const dateLabel = formatShortMDY(date).split('/').slice(0, 2).join('/');

    return `
      <div
        class="pro-availability_day-card-wrapper fade-in ${isPast ? 'is-past' : ''}"
        data-date="${date}"
        id="day-panel-${date}"
        style="animation-delay: ${animationDelay}ms;"
      >
        <div class="pro-availability_day-card">
          <div class="pro-availability_day-card-heading">
            <div class="pro-availability_weekday-value">${weekday} - </div>
            <div class="pro-availability_date-label">${dateLabel}</div>
          </div>
          <div class="pro-availability_day-card-body">
            ${isPast ? '' : this.buildProsList(displayPros)}
            ${isPast ? '' : this.buildShowMoreButton(date, hasMore)}
            ${isPast ? '' : this.buildEmptyMessage(date, pros.length === 0)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Builds professionals list HTML
   */
  private buildProsList(pros: ProfessionalSummary[]): string {
    if (pros.length === 0) return '';

    const items = pros.map((pro) => {
      const name = formatProfessionalName(pro);
      const href = buildProfileUrl(pro.user_uuid, this.config.utmContent);
      const positionAcr = getPositionAcronym(pro.position_type || '');
      const badge = positionAcr
        ? `<div class="pro-availability_position-tab" title="${escapeHtml(positionAcr.label)}" aria-label="${escapeHtml(positionAcr.label)}">${escapeHtml(positionAcr.abbr)}</div>`
        : '';

      return `
        <li>
          <a href="${escapeHtml(href)}" target="_blank" rel="noopener">${escapeHtml(name)}</a>
          ${badge}
        </li>
      `;
    });

    return `
      <ul class="pro-availability_pros-list w-list-unstyled" role="list">
        ${items.join('')}
      </ul>
    `;
  }

  /**
   * Builds "Show more" button HTML
   * On ondiem.com / staging Webflow: open modal on same page (href="#", data-open-modal).
   * Otherwise: redirect to URL from data-show-more-environment (staging/production) or data-show-more-url.
   */
  private buildShowMoreButton(date: string, hasMore: boolean): string {
    if (!hasMore) {
      return '<a data-date="" href="#" class="button is-text is-left-icon is-xsmall is-centered padding-0 pro-availability_show-more-btn hidden w-inline-block"></a>';
    }

    const onOndiem = typeof window !== 'undefined' && isOnDiemDomain();
    let href: string;
    if (onOndiem) {
      href = '#';
    } else {
      const baseUrl =
        this.config.showMoreEnvironment === 'staging'
          ? 'https://ondiem-staging.webflow.io/practices'
          : this.config.showMoreEnvironment === 'production'
            ? 'https://ondiem.com/practices'
            : (this.config.showMoreUrl || 'https://ondiem.com/practices');
      href = buildShowMoreUrl(
        baseUrl,
        date,
        this.config.zipCode,
        this.config.position,
        this.config.instance,
        this.config.utmContent
      );
    }
    const openModalAttr = onOndiem ? ' data-open-modal="1"' : '';

    return `
      <a
        data-date="${date}"
        href="${escapeHtml(href)}"
        ${openModalAttr}
        class="button is-text is-left-icon is-xsmall is-centered padding-0 pro-availability_show-more-btn w-inline-block"
      >
        <img src="${CDN_ASSETS.LAYERED_STACK_ICON}" loading="lazy" alt="">
        <div>Show more</div>
      </a>
    `;
  }

  /**
   * Builds empty message HTML
   */
  private buildEmptyMessage(date: string, isEmpty: boolean): string {
    if (!isEmpty) {
      return '<div class="pro-availability_empty-message hidden"></div>';
    }

    return `
      <div class="pro-availability_empty-message is-zero">
        <div class="empty-msg__top">
          <a
            data-analytics-cta="create_shift"
            data-date="${date}"
            href="https://app.ondiem.com/login"
            target="_blank"
            rel="noopener"
            class="text-style-link"
          >
            Create a shift
          </a>
          <br>to attract professionals
        </div>
        <div class="empty-msg__bottom">
          <a
            data-analytics-cta="learn_more"
            data-date="${date}"
            href="/landing/post-a-shift"
            class="text-size-tiny text-weight-semibold text-style-link"
          >
            Learn more
          </a>
        </div>
      </div>
    `;
  }
}
